// frontend/src/components/notifications/NotificationDropdown.jsx
import React, { useState } from 'react';
import { Dropdown, Badge, Button, ListGroup, Form } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import NotificationDrawer from './NotificationDrawer';
import styles from './NotificationDropdown.module.scss';

/**
 * 📥 DROPDOWN DE NOTIFICAÇÕES NO HEADER
 * 
 * Exibe lista completa de notificações com filtros,
 * ações em lote e navegação para detalhes.
 */

const NotificationDropdown = () => {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    isConnected,
    markAsRead,
    markAllAsRead,
    clearAll,
    clearRead,
    removeNotification,
    navigateToNotification
  } = useNotifications();

  const [showSettings, setShowSettings] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);

  // Mostrar apenas notificações NÃO LIDAS no dropdown (máximo 10)
  const filteredNotifications = notifications.filter(notification => !notification.read).slice(0, 10);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'agora mesmo';
    
    try {
      const now = new Date();
      const notificationDate = new Date(timestamp);
      const diff = now - notificationDate;
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);

      if (minutes < 1) return 'agora mesmo';
      if (minutes < 60) return `há ${minutes} min`;
      if (hours < 24) return `há ${hours}h`;
      if (days < 7) return `há ${days} dias`;
      return notificationDate.toLocaleDateString('pt-BR');
    } catch (error) {
      console.error('Erro ao formatar timestamp:', error, timestamp);
      return 'agora mesmo';
    }
  };

  const handleNotificationClick = (notification) => {
    // Marcar como lida
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    // Navegar para a página específica
    navigateToNotification(notification);
  };

  const CustomToggle = React.forwardRef(({ children, onClick }, ref) => {
    const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
    
    React.useEffect(() => {
      const handleThemeChange = () => {
        const currentTheme = document.documentElement.getAttribute('data-theme') || 'light';
        setTheme(currentTheme);
      };
      
      const observer = new MutationObserver(handleThemeChange);
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-theme']
      });
      
      handleThemeChange();
      return () => observer.disconnect();
    }, []);
    
    return (
      <button
        ref={ref}
        onClick={(e) => {
          e.preventDefault();
          onClick(e);
        }}
        className="d-flex justify-content-center align-items-center rounded-circle position-relative"
        style={{
          width: '40px',
          height: '40px',
          backgroundColor: theme === 'dark' ? '#2d2d2d' : '#f8f9fa',
          border: theme === 'dark' ? '1px solid #404040' : '1px solid #e9ecef',
          transition: 'all 0.2s ease',
          color: theme === 'dark' ? '#ffffff' : '#6c757d'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = theme === 'dark' ? '#404040' : '#e9ecef';
          e.currentTarget.style.transform = 'scale(1.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = theme === 'dark' ? '#2d2d2d' : '#f8f9fa';
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        <Icon icon="solar:bell-bold" style={{ fontSize: '20px', color: theme === 'dark' ? '#ffffff' : '#6c757d' }} />
        {unreadCount > 0 && (
          <Badge 
            pill 
            bg="danger" 
            className="position-absolute top-0 start-100 translate-middle"
            style={{ fontSize: '0.6rem', minWidth: '1.2rem' }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
        {!isConnected && (
          <span 
            className="position-absolute top-0 start-0 badge rounded-pill bg-warning"
            style={{ fontSize: '0.4rem', width: '8px', height: '8px' }}
            title="Conexão offline"
          />
        )}
      </button>
    );
  });

  // Marcar todas como lidas ao abrir o dropdown
  const handleDropdownToggle = (isOpen) => {
    if (isOpen && unreadCount > 0) {
      markAllAsRead();
    }
  };

  return (
    <Dropdown align="end" onToggle={handleDropdownToggle}>
      <Dropdown.Toggle as={CustomToggle} />

      <Dropdown.Menu
        style={{ 
          width: '650px', 
          maxHeight: '500px',
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
        className="shadow-lg border-0"
      >
        {/* Header Simplificado */}
        <div className="px-3 py-2 border-bottom">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0 d-flex align-items-center justify-content-between w-100">
              <span className="d-flex align-items-center">
                <Icon icon="mdi:bell" className="me-2" />
                Notificações
              </span>
              {unreadCount > 0 && (
                <Badge pill bg="primary">
                  {unreadCount}
                </Badge>
              )}
            </h6>
            

          </div>
        </div>

        {/* Lista de notificações */}
        <div style={{ maxHeight: '300px', overflowY: 'auto', overflowX: 'hidden', padding: '8px 0' }}>
          {filteredNotifications.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <Icon icon="mdi:bell-check" style={{ fontSize: '2rem' }} className="mb-2" />
              <p className="mb-0">Parabéns! 🎉</p>
              <small>Todas as notificações foram lidas</small>
            </div>
          ) : (
            <ListGroup variant="flush">
              {filteredNotifications.map(notification => (
                <ListGroup.Item
                  key={notification.id}
                  action
                  onClick={() => handleNotificationClick(notification)}
                  className={`${styles.notificationItem} ${!notification.read ? styles.unread : styles.read}`}
                >
                  <div className="d-flex">
                    <div className="flex-shrink-0 me-3">
                      <div className={`rounded-circle d-flex align-items-center justify-content-center ${notification.bgColor}`} 
                           style={{ 
                             width: '42px', 
                             height: '42px',
                             boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                           }}>
                        <Icon 
                          icon={notification.icon} 
                          className={`text-${notification.color}`}
                          style={{ fontSize: '1.3rem' }}
                        />
                      </div>
                    </div>
                    
                    <div className="flex-grow-1 min-width-0" style={{ overflow: 'hidden' }}>
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <span className="mb-0 fw-bold text-dark" style={{ 
                          fontSize: '0.9rem', 
                          lineHeight: '1.2',
                          wordWrap: 'break-word',
                          maxWidth: '70%'
                        }}>
                          {notification.title}
                          {!notification.read && (
                            <Badge pill bg="primary" className="ms-2" style={{ fontSize: '0.6rem' }}>
                              Nova
                            </Badge>
                          )}
                        </span>
                        <small className="text-muted flex-shrink-0 ms-2" style={{ 
                          fontSize: '0.75rem',
                          fontWeight: '500'
                        }}>
                          {formatTimestamp(notification.timestamp)}
                        </small>
                      </div>
                      
                      <p className="mb-2 text-muted" style={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        fontSize: '0.85rem',
                        lineHeight: '1.4',
                        marginTop: '4px',
                        wordWrap: 'break-word'
                      }}>
                        {notification.message}
                      </p>

                      {/* Card inteiro é clicável - sem botões */}
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </div>

        {/* Footer - Sempre visível */}
        <div className="px-3 py-2 border-top bg-light text-center">
          <Button 
            variant="link" 
            size="sm" 
            className="text-decoration-none fw-semibold"
            onClick={() => setShowDrawer(true)}
          >
            Ver todas as notificações
            {notifications.length > 0 && (
              <Badge pill bg="secondary" className="ms-2" style={{ fontSize: '0.6rem' }}>
                {notifications.length}
              </Badge>
            )}
          </Button>
        </div>
      </Dropdown.Menu>

      {/* Drawer de Todas as Notificações */}
      <NotificationDrawer 
        show={showDrawer} 
        onHide={() => setShowDrawer(false)} 
      />
    </Dropdown>
  );
};

export default NotificationDropdown;


